/**
 * GroupCourse Builder Edit Page - Sprint 1
 * 
 * Editor principal de cursos grupales con tabs:
 * 1. General - Metadata y configuración global
 * 2. 18 Semanas - Editor de semanas (placeholder Sprint 1, funcionará en Sprint 2)
 * 3. Coach IA - Configuración global de IA (Sprint 3)
 * 4. Publicación - Validación y publish (Sprint 4)
 * 
 * Sprint 1 scope: Solo tab General funcional, otros como placeholders
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md
 * @see SISTEMA_GRUPAL_DOCUMENTACION_COMPLETA.md
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Save, 
  Loader,
  AlertCircle,
  ArrowLeft,
  Settings,
  Calendar,
  Brain,
  Upload,
  Info,
  Eye,
  EyeOff,
  RotateCcw,
  Archive,
  MoreVertical
} from 'lucide-react';
import { 
  getGroupCourse, 
  updateGroupCourse,
  updateWeek,
  duplicateWeek,
  publishCourse,
  hideCourse,
  showCourse,
  unpublishCourse,
  archiveCourse,
  restoreCourse
} from '../../services/adminGroupCoursesApi';
import apiClient from '../../services/apiClient';
import { useAuth } from '../../context/AuthContext';
import WeeksOverview from './WeeksOverview';
import WeekEditorModal from './WeekEditorModal';

const GroupCourseBuilderEditPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Week editor modal states
  const [weekModalOpen, setWeekModalOpen] = useState(false);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState(null);
  const [selectedWeekData, setSelectedWeekData] = useState(null);

  // Form data (solo tab General)
  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    estimatedHours: 72,
    skills: [],
    groupCourseConfig: {
      totalWeeks: 18,
      minGroupSize: 3,
      maxGroupSize: 8,
      enableCrossFeedback: true,
      enableAICoach: true,
      weights: {
        deliverable: 50,
        crossFeedback: 30,
        aiCoachFeedback: 20
      }
    }
  });

  // Verificar permisos
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'teacher') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Cargar curso al montar
  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getGroupCourse(courseId);
      setCourse(response.course);
      
      // Poblar form data
      setFormData({
        title: response.course.title || '',
        shortDescription: response.course.shortDescription || '',
        estimatedHours: response.course.estimatedHours || 72,
        skills: response.course.skills || [],
        groupCourseConfig: response.course.groupCourseConfig || {
          totalWeeks: 18,
          minGroupSize: 3,
          maxGroupSize: 8,
          enableCrossFeedback: true,
          enableAICoach: true,
          weights: {
            deliverable: 50,
            crossFeedback: 30,
            aiCoachFeedback: 20
          }
        }
      });
    } catch (err) {
      console.error('Error cargando curso:', err);
      setError('No se pudo cargar el curso');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Validar weights
      const weightSum = formData.groupCourseConfig.weights.deliverable +
                       formData.groupCourseConfig.weights.crossFeedback +
                       formData.groupCourseConfig.weights.aiCoachFeedback;
      
      if (weightSum !== 100) {
        setSaveError(`Los pesos deben sumar 100%. Actual: ${weightSum}%`);
        setSaving(false);
        return;
      }

      await updateGroupCourse(courseId, formData);
      setSaveSuccess(true);
      
      // Refrescar curso
      await loadCourse();
      
      // Ocultar mensaje después de 3 segundos
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Error guardando cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleSkillsChange = (e) => {
    const skillsArray = e.target.value.split(',').map(s => s.trim()).filter(s => s);
    setFormData({ ...formData, skills: skillsArray });
  };

  // Week editor handlers
  const handleEditWeek = (weekNumber) => {
    const weekData = course.weeks?.find(w => w.weekNumber === weekNumber);
    setSelectedWeekNumber(weekNumber);
    setSelectedWeekData(weekData || null);
    setWeekModalOpen(true);
  };

  const handleSaveWeek = async (weekNumber, weekData) => {
    try {
      await updateWeek(courseId, weekNumber, weekData);
      // Refrescar curso para actualizar vista
      await loadCourse();
      setWeekModalOpen(false);
    } catch (err) {
      // Incluir el mensaje detallado de Mongoose si está disponible
      const detail = err.response?.data?.message || err.response?.data?.error || err.message || 'Error guardando semana';
      throw new Error(detail);
    }
  };

  const handleDuplicateWeek = async (sourceWeek, targetWeek) => {
    if (!confirm(`¿Duplicar semana ${sourceWeek} a semana ${targetWeek}?`)) {
      return;
    }

    try {
      await duplicateWeek(courseId, sourceWeek, targetWeek);
      await loadCourse(); // Refrescar
      alert(`Semana ${sourceWeek} duplicada a semana ${targetWeek} exitosamente`);
    } catch (err) {
      alert(err.response?.data?.error || 'Error duplicando semana');
    }
  };

  // Validation and publication handlers
  const validateCourse = () => {
    if (!course) return null;

    const errors = [];
    const warnings = [];
    
    // Check if we have 18 weeks
    const weekCount = course.weeks?.length || 0;
    if (weekCount < 18) {
      errors.push(`Faltan ${18 - weekCount} semanas. Necesitas 18 semanas completas.`);
    }

    // Check each week for B2 templates and criteria weights
    const weeks = course.weeks || [];
    weeks.forEach(week => {
      // Check B2 template
      if (!week.b2_execution_template || !week.b2_execution_template.fields || week.b2_execution_template.fields.length === 0) {
        errors.push(`Semana ${week.weekNumber}: Falta template B2 (Ejecución Supervisada)`);
      }

      // Check B3 criteria weights (ERROR CRÍTICO)
      if (week.b3_criteria && week.b3_criteria.length > 0) {
        const b3Total = week.b3_criteria.reduce((sum, criteria) => sum + (criteria.weight || 0), 0);
        if (Math.abs(b3Total - 100) > 0.01) {
          errors.push(`Semana ${week.weekNumber}: Pesos B3 deben sumar 100% (actual: ${b3Total.toFixed(2)}%)`);
        }
      }

      // Check B6 criteria weights (ERROR CRÍTICO)
      if (week.b6_criteria && week.b6_criteria.length > 0) {
        const b6Total = week.b6_criteria.reduce((sum, criteria) => sum + (criteria.weight || 0), 0);
        if (Math.abs(b6Total - 100) > 0.01) {
          errors.push(`Semana ${week.weekNumber}: Pesos B6 deben sumar 100% (actual: ${b6Total.toFixed(2)}%)`);
        }
      }
    });

    return {
      valid: errors.length === 0,
      canPublish: errors.length === 0,
      errors,
      warnings,
      stats: {
        totalWeeks: weekCount,
        requiredWeeks: 18,
        completionPercentage: Math.round((weekCount / 18) * 100)
      }
    };
  };

  const handleValidateCourse = () => {
    setValidating(true);
    
    // Simulate validation delay
    setTimeout(() => {
      const result = validateCourse();
      setValidationResult(result);
      setValidating(false);
    }, 1000);
  };

  const handlePublishCourse = async () => {
    if (!validationResult?.canPublish) {
      alert('No se puede publicar. Corrige los errores primero.');
      return;
    }

    const confirmPublish = confirm(
      '¿Estás seguro de que deseas publicar este curso?\n\n' +
      'Una vez publicado:\n' +
      '• Los estudiantes podrán matricularse\n' +
      '• El curso será visible públicamente\n' +
      '• Seguirás pudiendo editarlo'
    );

    if (!confirmPublish) return;

    setPublishing(true);
    
    try {
      // Use the API service for publishing
      await publishCourse(courseId);
      
      alert('¡Curso publicado exitosamente!');
      
      // Refresh course data to show new status
      await loadCourse();
      
      // Reset validation result
      setValidationResult(null);
      
    } catch (err) {
      console.error('Error publishing course:', err);
      alert('Error al publicar el curso: ' + (err.response?.data?.error || err.message));
    } finally {
      setPublishing(false);
    }
  };

  // State Management Functions
  const handleHideCourse = async () => {
    const confirmHide = confirm(
      '¿Deseas ocultar este curso?\n\n' +
      'El curso seguirá publicado pero no será visible para los estudiantes en el catálogo.'
    );
    
    if (!confirmHide) return;

    try {
      await hideCourse(courseId);
      alert('Curso ocultado exitosamente');
      await loadCourse();
    } catch (err) {
      console.error('Error hiding course:', err);
      alert('Error al ocultar el curso: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleShowCourse = async () => {
    try {
      await showCourse(courseId);
      alert('Curso visible exitosamente');
      await loadCourse();
    } catch (err) {
      console.error('Error showing course:', err);
      alert('Error al mostrar el curso: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUnpublishCourse = async () => {
    const confirmUnpublish = confirm(
      '¿Deseas despublicar este curso?\n\n' +
      'ATENCIÓN: Esta acción:\n' +
      '• Cambiará el curso a estado "Borrador"\n' +
      '• Los estudiantes no podrán acceder al curso\n' +
      '• Solo se permite si no hay matriculados (en futuras versiones)\n\n' +
      '¿Continuar?'
    );
    
    if (!confirmUnpublish) return;

    try {
      await unpublishCourse(courseId);
      alert('Curso despublicado exitosamente');
      await loadCourse();
      setValidationResult(null); // Reset validation since it's back to draft
    } catch (err) {
      console.error('Error unpublishing course:', err);
      alert('Error al despublicar el curso: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleArchiveCourse = async () => {
    const confirmArchive = confirm(
      '¿Deseas archivar este curso?\n\n' +
      'ATENCIÓN: Esta acción:\n' +
      '• Ocultará completamente el curso del sistema\n' +
      '• Los estudiantes matriculados no podrán acceder\n' +
      '• Solo administradores podrán ver cursos archivados\n' +
      '• Puedes restaurarlo cuando desees\n\n' +
      '¿Continuar?'
    );
    
    if (!confirmArchive) return;

    try {
      await archiveCourse(courseId);
      alert('Curso archivado exitosamente');
      // Redirect to course list since archived courses shouldn't be actively edited
      navigate('/admin/group-course-builder');
    } catch (err) {
      console.error('Error archiving course:', err);
      alert('Error al archivar el curso: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRestoreCourse = async () => {
    const confirmRestore = confirm(
      '¿Deseas restaurar este curso archivado?\n\n' +
      'Esta acción:\n' +
      '• Cambiará el curso de "Archivado" a "Borrador"\n' +
      '• Podrás editarlo y republicarlo si deseas\n' +
      '• No será visible para estudiantes hasta que lo publiques nuevamente'
    );
    
    if (!confirmRestore) return;

    try {
      await restoreCourse(courseId);
      alert('Curso restaurado exitosamente');
      await loadCourse();
    } catch (err) {
      console.error('Error restoring course:', err);
      alert('Error al restaurar el curso: ' + (err.response?.data?.error || err.message));
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'weeks', label: '18 Semanas', icon: Calendar },
    { id: 'coach', label: 'Coach IA', icon: Brain },
    { id: 'publish', label: 'Publicación', icon: Upload }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <div>
            <h3 className="font-semibold text-red-900">Error</h3>
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => navigate('/admin/group-course-builder')}
              className="mt-4 text-red-700 underline text-sm"
            >
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/group-course-builder')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {course?.title || 'Cargando...'}
                </h1>
                <p className="text-sm text-gray-500">
                  Course ID: {courseId} • Status: {course?.status || 'draft'}
                </p>
              </div>
            </div>
            
            {/* Stats and Actions */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-6 text-sm">
                <div className="text-center">
                  <p className="text-gray-500">Semanas</p>
                  <p className="font-bold text-gray-900">
                    {course?.weeks?.length || 0}/{course?.groupCourseConfig?.totalWeeks || 18}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500">Completitud</p>
                  <p className="font-bold text-purple-600">
                    {Math.round(((course?.weeks?.length || 0) / (course?.groupCourseConfig?.totalWeeks || 18)) * 100)}%
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/admin/group-course-builder/${courseId}/preview`)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab: General */}
        {activeTab === 'general' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Configuración General
            </h2>

            {/* Messages */}
            {saveSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-700 text-sm">Cambios guardados exitosamente</p>
              </div>
            )}
            {saveError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-700 text-sm">{saveError}</p>
              </div>
            )}

            <div className="space-y-6">
              {/* Metadata */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título del Curso
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción Corta
                </label>
                <textarea
                  value={formData.shortDescription}
                  onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Horas Estimadas
                  </label>
                  <input
                    type="number"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData({ ...formData, estimatedHours: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Semanas
                  </label>
                  <input
                    type="number"
                    value={formData.groupCourseConfig.totalWeeks}
                    onChange={(e) => setFormData({
                      ...formData,
                      groupCourseConfig: {
                        ...formData.groupCourseConfig,
                        totalWeeks: parseInt(e.target.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="1"
                    max="52"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills (separadas por coma)
                </label>
                <input
                  type="text"
                  value={formData.skills.join(', ')}
                  onChange={handleSkillsChange}
                  placeholder="ej: Planificación, Liderazgo, Comunicación"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Configuración de Grupos */}
              <div className="border-t pt-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Configuración de Grupos
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tamaño Mínimo
                    </label>
                    <input
                      type="number"
                      value={formData.groupCourseConfig.minGroupSize}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupCourseConfig: {
                          ...formData.groupCourseConfig,
                          minGroupSize: parseInt(e.target.value)
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      min="2"
                      max="20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tamaño Máximo
                    </label>
                    <input
                      type="number"
                      value={formData.groupCourseConfig.maxGroupSize}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupCourseConfig: {
                          ...formData.groupCourseConfig,
                          maxGroupSize: parseInt(e.target.value)
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      min="2"
                      max="20"
                    />
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.groupCourseConfig.enableCrossFeedback}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupCourseConfig: {
                          ...formData.groupCourseConfig,
                          enableCrossFeedback: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Habilitar Cross-Feedback (B6)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.groupCourseConfig.enableAICoach}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupCourseConfig: {
                          ...formData.groupCourseConfig,
                          enableAICoach: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Habilitar Coach IA (B4)</span>
                  </label>
                </div>
              </div>

              {/* Pesos de Evaluación */}
              <div className="border-t pt-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Pesos de Evaluación Final (deben sumar 100%)
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deliverable (%)
                    </label>
                    <input
                      type="number"
                      value={formData.groupCourseConfig.weights.deliverable}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupCourseConfig: {
                          ...formData.groupCourseConfig,
                          weights: {
                            ...formData.groupCourseConfig.weights,
                            deliverable: parseInt(e.target.value) || 0
                          }
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cross-Feedback (%)
                    </label>
                    <input
                      type="number"
                      value={formData.groupCourseConfig.weights.crossFeedback}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupCourseConfig: {
                          ...formData.groupCourseConfig,
                          weights: {
                            ...formData.groupCourseConfig.weights,
                            crossFeedback: parseInt(e.target.value) || 0
                          }
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Coach IA (%)
                    </label>
                    <input
                      type="number"
                      value={formData.groupCourseConfig.weights.aiCoachFeedback}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupCourseConfig: {
                          ...formData.groupCourseConfig,
                          weights: {
                            ...formData.groupCourseConfig.weights,
                            aiCoachFeedback: parseInt(e.target.value) || 0
                          }
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  <span className="text-gray-700">Suma total: </span>
                  <span className={`font-bold ${
                    (formData.groupCourseConfig.weights.deliverable +
                     formData.groupCourseConfig.weights.crossFeedback +
                     formData.groupCourseConfig.weights.aiCoachFeedback) === 100
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {formData.groupCourseConfig.weights.deliverable +
                     formData.groupCourseConfig.weights.crossFeedback +
                     formData.groupCourseConfig.weights.aiCoachFeedback}%
                  </span>
                </div>
              </div>

              {/* Acciones */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  onClick={() => navigate('/admin/group-course-builder')}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveGeneral}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Guardar Cambios
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab: 18 Semanas (Sprint 2 - FUNCTIONAL) */}
        {activeTab === 'weeks' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <WeeksOverview
              course={course}
              onEditWeek={handleEditWeek}
              onDuplicateWeek={(weekNumber) => {
                const targetWeek = prompt(`¿A qué semana quieres duplicar la semana ${weekNumber}? (1-${course.groupCourseConfig.totalWeeks})`);
                if (targetWeek) {
                  const target = parseInt(targetWeek);
                  if (target >= 1 && target <= course.groupCourseConfig.totalWeeks) {
                    handleDuplicateWeek(weekNumber, target);
                  } else {
                    alert(`Número de semana inválido. Debe estar entre 1 y ${course.groupCourseConfig.totalWeeks}`);
                  }
                }
              }}
            />
          </div>
        )}

        {/* Tab: Coach IA (Placeholder Sprint 3) */}
        {activeTab === 'coach' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Configuración Global de Coach IA
            </h3>
            <p className="text-gray-600 mb-4">
              Personalización del comportamiento del Coach IA
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 inline-flex items-start gap-3 text-left">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold">Sprint 3 - Próximamente</p>
                <p className="mt-1">Configuración avanzada del Coach IA:</p>
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  <li>Prompt base global del coach</li>
                  <li>Frecuencia de evaluaciones automáticas</li>
                  <li>Áreas de enfoque por defecto</li>
                  <li>Estilo de feedback (técnico, motivacional, equilibrado)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Publicación - IMPLEMENTADO */}
        {activeTab === 'publish' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Upload className="w-6 h-6 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Validación y Publicación
                </h3>
              </div>
              <p className="text-gray-600">
                Verifica que el curso esté completo antes de publicar
              </p>
            </div>

            <div className="p-6">
              {/* Current Status */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Estado Actual</h4>
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Estado:</span>
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          course?.status === 'published' ? 'bg-green-100 text-green-800' :
                          course?.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {course?.status === 'published' ? 'Publicado' : 
                           course?.status === 'archived' ? 'Archivado' : 'Borrador'}
                        </span>
                      </div>
                      
                      {course?.status === 'published' && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Visibilidad:</span>
                          <span className={`px-2 py-1 rounded text-sm font-medium ${
                            course?.isVisible ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {course?.isVisible ? 'Visible' : 'Oculto'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-1">
                      Course ID: {courseId}
                      {course?.publishedAt && (
                        <> • Publicado: {new Date(course.publishedAt).toLocaleDateString()}</>
                      )}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Semanas configuradas</p>
                    <p className="text-lg font-semibold text-purple-600">
                      {course?.weeks?.length || 0} / 18
                    </p>
                  </div>
                </div>

                {/* State Management Controls for Published Courses */}
                {course?.status === 'published' && (
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-medium text-gray-900">Gestión de Estado</h5>
                      <div className="flex gap-2">
                        {/* Visibility Toggle */}
                        <button
                          onClick={course?.isVisible ? handleHideCourse : handleShowCourse}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                            course?.isVisible 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {course?.isVisible ? (
                            <>
                              <EyeOff className="w-4 h-4 inline mr-1" />
                              Ocultar
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 h-4 inline mr-1" />
                              Mostrar
                            </>
                          )}
                        </button>

                        {/* Unpublish */}
                        <button
                          onClick={handleUnpublishCourse}
                          className="px-3 py-1.5 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded text-sm font-medium transition-colors"
                        >
                          <RotateCcw className="w-4 h-4 inline mr-1" />
                          Despublicar
                        </button>

                        {/* Archive */}
                        <button
                          onClick={handleArchiveCourse}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded text-sm font-medium transition-colors"
                        >
                          <Archive className="w-4 h-4 inline mr-1" />
                          Archivar
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Restore Control for Archived Courses */}
                {course?.status === 'archived' && (
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-sm font-medium text-gray-900">Curso Archivado</h5>
                        <p className="text-xs text-gray-600">Este curso está archivado y no es visible para estudiantes</p>
                      </div>
                      <button
                        onClick={handleRestoreCourse}
                        className="px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded text-sm font-medium transition-colors"
                      >
                        <RotateCcw className="w-4 h-4 inline mr-1" />
                        Restaurar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Validation Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Validación del Curso</h4>
                  <button
                    onClick={handleValidateCourse}
                    disabled={validating}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg flex items-center gap-2 transition-colors"
                  >
                    {validating ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Validando...
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        Validar Curso
                      </>
                    )}
                  </button>
                </div>

                {/* Validation Results */}
                {validationResult && (
                  <div className={`border rounded-lg p-4 ${validationResult.canPublish ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${validationResult.canPublish ? 'bg-green-100' : 'bg-red-100'}`}>
                        {validationResult.canPublish ? (
                          <span className="text-lg">✓</span>
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h5 className={`font-semibold ${validationResult.canPublish ? 'text-green-900' : 'text-red-900'}`}>
                          {validationResult.canPublish ? '✓ Curso Listo para Publicar' : '❌ Curso No Listo'}
                        </h5>
                        <p className={`text-sm mt-1 ${validationResult.canPublish ? 'text-green-700' : 'text-red-700'}`}>
                          {validationResult.canPublish 
                            ? 'Todas las validaciones pasaron correctamente. Puedes publicar el curso.'
                            : `Se encontraron ${validationResult.errors.length} error(es) que deben ser corregidos.`
                          }
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-white rounded border">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Semanas</p>
                        <p className="font-semibold text-lg">
                          {validationResult.stats.totalWeeks}/18
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Completitud</p>
                        <p className="font-semibold text-lg">
                          {validationResult.stats.completionPercentage}%
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Errores</p>
                        <p className="font-semibold text-lg text-red-600">
                          {validationResult.errors.length}
                        </p>
                      </div>
                    </div>

                    {/* Errors */}
                    {validationResult.errors.length > 0 && (
                      <div className="mb-4">
                        <h6 className="font-medium text-red-900 mb-2">Errores que deben corregirse:</h6>
                        <ul className="space-y-1">
                          {validationResult.errors.map((error, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-red-800">
                              <span className="text-red-500 font-bold">•</span>
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Warnings */}
                    {validationResult.warnings.length > 0 && (
                      <div>
                        <h6 className="font-medium text-orange-900 mb-2">Advertencias (no críticas):</h6>
                        <ul className="space-y-1">
                          {validationResult.warnings.map((warning, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-orange-800">
                              <span className="text-orange-500 font-bold">•</span>
                              {warning}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Publication Section */}
                {validationResult && (
                  <div className="border-t pt-6 mt-6">
                    <div className="text-center space-y-4">
                      {validationResult.canPublish ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          {course?.status === 'published' ? (
                            <>
                              <h5 className="font-medium text-green-900 mb-2">✅ Curso Publicado y Validado</h5>
                              <p className="text-sm text-green-700 mb-4">
                                El curso está publicado y todas las validaciones pasaron correctamente.
                                {course?.isVisible 
                                  ? ' Los estudiantes pueden verlo y matricularse.' 
                                  : ' Está oculto para estudiantes (usa los controles de arriba para mostrarlo).'}
                              </p>
                              <div className="text-xs text-green-600 bg-green-100 rounded px-3 py-2">
                                💡 Tip: Puedes seguir editando el curso. Los cambios se guardan automáticamente.
                              </div>
                            </>
                          ) : (
                            <>
                              <h5 className="font-medium text-green-900 mb-2">🚀 ¡Listo para Publicar!</h5>
                              <p className="text-sm text-green-700 mb-4">
                                El curso ha pasado todas las validaciones y está listo para ser publicado.
                              </p>
                              <button
                                onClick={handlePublishCourse}
                                disabled={publishing}
                                className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium flex items-center gap-2 mx-auto transition-colors"
                              >
                                {publishing ? (
                                  <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    Publicando...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-5 h-5" />
                                    Publicar Curso
                                  </>
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h5 className="font-medium text-red-900 mb-2">❌ No se puede publicar</h5>
                          <p className="text-sm text-red-700">
                            Corrige los errores mostrados arriba antes de publicar el curso.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="border-t pt-6 mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">Acciones Rápidas</h4>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setActiveTab('weeks')}
                      className="px-4 py-2 border border-gray-300 hover:border-gray-400 rounded-lg text-sm flex items-center gap-2 transition-colors"
                    >
                      <Calendar className="w-4 h-4" />
                      Ver Semanas
                    </button>
                    <button
                      onClick={() => navigate(`/admin/group-course-builder/${courseId}/preview`)}
                      className="px-4 py-2 border border-gray-300 hover:border-gray-400 rounded-lg text-sm flex items-center gap-2 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      Vista Previa
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Week Editor Modal */}
      <WeekEditorModal
        isOpen={weekModalOpen}
        onClose={() => setWeekModalOpen(false)}
        weekNumber={selectedWeekNumber}
        weekData={selectedWeekData}
        courseId={courseId}
        onSave={handleSaveWeek}
      />
    </div>
  );
};

export default GroupCourseBuilderEditPage;
